import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, ShieldCheck } from "lucide-react";
import { COMPANY, SERVICES, LOCALITIES } from "@/data/site";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [sub, setSub] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email");
    setSub(true);
    try {
      await subscribeNewsletter(email);
      toast.success("Subscribed! Watch out for our waterproofing tips.");
      setEmail("");
    } catch { toast.error("Try again later"); } finally { setSub(false); }
  };

  return (
    <footer className="bg-navy-deep text-slate-300 grain relative" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-20 pb-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <img src="/logo.png" alt="JAL RAKSHAKK" className="h-12 w-12 object-contain" />
              <div>
                <div className="font-display font-extrabold text-white text-xl">JAL RAKSHAKK</div>
                <div className="text-[10px] tracking-[0.25em] text-sky uppercase">Seal Today, Safe Tomorrow</div>
              </div>
            </div>
            <p className="text-sm leading-relaxed max-w-sm text-slate-400">
              Jaipur's trusted premium waterproofing experts. 12+ years of engineering expertise, up to 15-year warranty, protecting residential, commercial & industrial properties across the city.
            </p>
            <form onSubmit={subscribe} className="mt-6 flex gap-2 max-w-sm" data-testid="newsletter-form">
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for waterproofing tips" className="flex-1 bg-white/5 border border-white/15 rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky" data-testid="newsletter-email" />
              <button className="btn-ripple bg-royal text-white px-4 rounded-sm" data-testid="newsletter-submit" disabled={sub}><Send className="h-4 w-4" /></button>
            </form>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm tracking-wide uppercase">Services</h4>
            <ul className="space-y-2 text-sm">
              {SERVICES.slice(0, 7).map((s) => (
                <li key={s.slug}><Link to={`/services/${s.slug}`} className="hover:text-sky transition-colors">{s.title}</Link></li>
              ))}
              <li><Link to="/services" className="text-sky font-medium">All Services →</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm tracking-wide uppercase">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-sky">About Us</Link></li>
              <li><Link to="/gallery" className="hover:text-sky">Project Gallery</Link></li>
              <li><Link to="/leak-detection" className="hover:text-sky">Leak Detection Tool</Link></li>
              <li><Link to="/cost-estimator" className="hover:text-sky">Cost Estimator</Link></li>
              <li><Link to="/blog" className="hover:text-sky">Blog</Link></li>
              <li><Link to="/contact" className="hover:text-sky">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm tracking-wide uppercase">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2"><Phone className="h-4 w-4 text-sky mt-0.5" /><a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-sky">{COMPANY.phone}</a></li>
              <li className="flex items-start gap-2"><MessageCircle className="h-4 w-4 text-sky mt-0.5" /><a href={`https://wa.me/${COMPANY.phoneRaw}`} className="hover:text-sky">WhatsApp</a></li>
              <li className="flex items-start gap-2"><Mail className="h-4 w-4 text-sky mt-0.5" /><a href={`mailto:${COMPANY.email}`} className="hover:text-sky break-all">{COMPANY.email}</a></li>
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 text-sky mt-0.5" /><span>{COMPANY.city}</span></li>
              <li className="flex items-start gap-2"><Clock className="h-4 w-4 text-sky mt-0.5" /><span>24/7 Emergency Support</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10">
          <div className="text-xs text-slate-500 mb-4">Serving all Jaipur:</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
            {LOCALITIES.map((l) => <span key={l}>{l} •</span>)}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sky" /> © {new Date().getFullYear()} JAL RAKSHAKK Waterproofing. All rights reserved.</div>
          <div className="flex gap-5">
            <Link to="/contact" className="hover:text-sky">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-sky">Terms</Link>
            <Link to="/services" className="hover:text-sky">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
